/**
 * FASE 20.4 — TOOL EXECUTION ENGINE
 *
 * Validates schema + permissions, executes the correct adapter,
 * applies result sanitization, and returns a normalized result envelope.
 *
 * Returns: { ok, tool, rows, meta: { count, latency, cached, error? } }
 */

import { getDataToolById }              from "./toolSchemas";
import { resolvePermissionScope, isRoleAllowed } from "./permissionFilters";
import { buildCacheKey, cacheGet, cacheSet }      from "./runtimeCache";
import { sanitizeRows }                           from "./resultSanitizers";

import { getTodayVisits, getVisitTimeline, getChurchVisitActivity, getUpcomingVisits, getNextVisit }
  from "./queryAdapters/visitsAdapter";
import { getUpcomingEvents }
  from "./queryAdapters/eventsAdapter";
import { getFamilyOverview, getFamiliesNeedingFollowup, getChurchFamilySummary }
  from "./queryAdapters/familiesAdapter";
import { getMemberStatus, getPeopleNeedingFollowup }
  from "./queryAdapters/peopleAdapter";
import { getRecentPrayers }
  from "./queryAdapters/prayersAdapter";
import { getChurchActivitySummary }
  from "./queryAdapters/churchesAdapter";
import { getUpcomingPreachings }
  from "./queryAdapters/preachingsAdapter";

/* ─── Adapter router ─────────────────────────────────────────── */

async function routeToAdapter(toolId, scope, filters) {
  switch (toolId) {
    case "get_today_visits":
      return getTodayVisits(scope);

    case "get_upcoming_events":
      return getUpcomingEvents(scope);

    case "get_family_overview":
      if (!filters?.entity_id) return [];
      const row = await getFamilyOverview(filters.entity_id);
      return row ? [row] : [];

    case "get_recent_prayers":
      return getRecentPrayers(scope);

    case "get_people_needing_followup":
      return getPeopleNeedingFollowup(scope);

    case "get_church_activity_summary":
      return getChurchActivitySummary(scope);

    case "get_member_status":
      return getMemberStatus(scope);

    case "get_visit_timeline":
      return getVisitTimeline(scope, filters);

    case "get_dashboard_snapshot": {
      // Composite — 3 sub-queries in parallel (Supabase uses HTTP/2, safe to parallelize)
      const [visits, events, prayers] = await Promise.all([
        getTodayVisits(scope),
        getUpcomingEvents(scope, 7),
        getRecentPrayers(scope),
      ]);
      return [
        ...visits.slice(0, 8).map(r  => ({ ...r, _source: "visit" })),
        ...events.slice(0, 5).map(r  => ({ ...r, _source: "event" })),
        ...prayers.slice(0, 5).map(r => ({ ...r, _source: "prayer" })),
      ];
    }

    case "get_next_visit":
      return getNextVisit(scope);

    case "get_upcoming_visits":
      return getUpcomingVisits(scope);

    case "get_week_schedule": {
      // Composite: upcoming pending visits + upcoming events, sorted by date
      const [weekVisits, weekEvents] = await Promise.all([
        getUpcomingVisits(scope, 7),
        getUpcomingEvents(scope, 7),
      ]);
      return [
        ...weekVisits.slice(0, 10).map((r) => ({ ...r, _source: "visit" })),
        ...weekEvents.slice(0, 8).map((r)  => ({ ...r, _source: "event" })),
      ].sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""));
    }

    case "get_upcoming_preachings":
      return getUpcomingPreachings(scope);

    default:
      throw new Error(`Unknown tool: ${toolId}`);
  }
}

/* ─── Public API ─────────────────────────────────────────────── */

/**
 * Execute a single data tool call.
 *
 * @param {Object} params
 * @param {string} params.toolId
 * @param {Object} [params.filters]        — entity_id, date_range, etc.
 * @param {Object} [params.appScope]       — from useKeviaContext().appScope
 * @returns {Promise<{ ok: boolean, tool: string, rows: Object[], meta: Object }>}
 */
export async function executeTool({ toolId, filters = {}, appScope = {} }) {
  const startedAt = Date.now();

  const schema = getDataToolById(toolId);
  if (!schema) {
    return { ok: false, tool: toolId, rows: [], meta: { error: "unknown_tool", latency: 0, cached: false, count: 0 } };
  }

  const scope = resolvePermissionScope(appScope);

  // Role check
  if (!isRoleAllowed(schema, scope.role)) {
    return { ok: false, tool: toolId, rows: [], meta: { error: "permission_denied", latency: 0, cached: false, count: 0 } };
  }

  // Offline check
  if (!schema.offlineCompatible && typeof navigator !== "undefined" && !navigator.onLine) {
    return { ok: false, tool: toolId, rows: [], meta: { error: "offline", latency: 0, cached: false, count: 0 } };
  }

  // Cache lookup
  const cacheKey = buildCacheKey(toolId, { ...filters, _scope: scope.level, _scope_id: scope.church_id ?? scope.district_id });
  if (schema.cacheTTL > 0) {
    const hit = cacheGet(cacheKey);
    if (hit) {
      return {
        ok:   true,
        tool: toolId,
        rows: hit.data,
        meta: { count: hit.data.length, latency: Date.now() - startedAt, cached: true },
      };
    }
  }

  // Execute
  try {
    const raw  = await routeToAdapter(toolId, scope, filters);
    const rows = sanitizeRows(raw).slice(0, schema.maxRows);

    if (schema.cacheTTL > 0) {
      cacheSet(cacheKey, rows, schema.cacheTTL, toolId);
    }

    return {
      ok:   true,
      tool: toolId,
      rows,
      meta: { count: rows.length, latency: Date.now() - startedAt, cached: false },
    };
  } catch (err) {
    return {
      ok:   false,
      tool: toolId,
      rows: [],
      meta: { error: err?.message ?? "adapter_error", latency: Date.now() - startedAt, cached: false, count: 0 },
    };
  }
}
